/**
 * @fileoverview A conversa de teste de um agente de IA (POST
 * /ai-agents/:agentId/test-chat, com a versão quando dada: a nova do
 * assistente, antes de entrar no ar): as mensagens, o envio com o histórico e o
 * "pensando". Usada pela aba de teste do editor de agentes e pelo assistente
 * que cria o agente (cada um com a sua tela).
 */

import { useCallback, useRef, useState } from "react";
import api from "../services/api";
import toastError from "../errors/toastError";

const useTestChat = (agentId, { errorText = "Erro ao processar mensagem. Verifique se o agente tem um prompt configurado.", versionId } = {}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  // O histórico vai com cada envio: lido daqui, sem esperar o estado do React.
  const historyRef = useRef([]);
  // O mesmo para o "pensando": um envio logo depois de limpar não fica preso no estado antigo.
  const loadingRef = useRef(false);
  // Cada conversa nova (clear) descarta a resposta que ainda vinha da anterior.
  const generationRef = useRef(0);

  const send = useCallback(async (raw) => {
    const text = String(raw || "").trim();
    if (!text || loadingRef.current || !agentId) return false;

    const generation = generationRef.current;
    const userMsg = { id: Date.now(), role: "user", content: text, timestamp: Date.now() };
    const history = historyRef.current.map((m) => ({ role: m.role, content: m.content }));
    historyRef.current = [...historyRef.current, userMsg];
    setMessages((prev) => [...prev, userMsg]);
    loadingRef.current = true;
    setLoading(true);

    try {
      const body = versionId ? { message: text, history, versionId } : { message: text, history };
      const { data } = await api.post(`/ai-agents/${agentId}/test-chat`, body, { timeout: 180000 });
      if (generation !== generationRef.current) return true;
      const replies = (data.messages || []).map((content, i) => ({
        id: Date.now() + i + 1, role: "assistant",
        content,
        confidence: data.confidence,
        wouldNeedApproval: data.wouldNeedApproval,
        files: i === 0 ? data.files : undefined,
        debug: i === 0 ? data.debug : undefined,
        timestamp: Date.now(),
      }));
      historyRef.current = [...historyRef.current, ...replies];
      setMessages((prev) => [...prev, ...replies]);
    } catch (err) {
      if (generation !== generationRef.current) return true;
      toastError(err);
      // A pergunta sem resposta fica na tela, mas não vai no histórico do próximo envio.
      historyRef.current = historyRef.current.filter((m) => m !== userMsg);
      setMessages((prev) => [...prev, { id: Date.now() + 999, role: "assistant", content: errorText, isError: true, timestamp: Date.now() }]);
    } finally {
      if (generation === generationRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
    return true;
  }, [agentId, versionId, errorText]);

  const clear = useCallback(() => {
    generationRef.current += 1;
    historyRef.current = [];
    loadingRef.current = false;
    setLoading(false);
    setMessages([]);
  }, []);

  return { messages, loading, send, clear };
};

export default useTestChat;
